import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { Agent, AgentCapability, AgentCollectorRun, AgentVersion, AuditLog, AuthPayload, BMCCredentialStatus, Customer, Datacenter, Device, DiscoveryJob, HardwareProfile, IPAddress, IPAddressType, IPGroup, IPPool, LifecycleEvent, LifecycleState, LogicalVolume, OperationLog, OSCredentialStatus, Page, PhysicalDisk, Rack, Role, ServerDiscovery, StorageController, Tenant, User } from '@/types/api'

const adminPassword = 'NewDCIM_Admin_2026_Local'

const roles: Role[] = [
  { id: 'role-super-admin', code: 'super_admin', name: 'Super Administrator', built_in: true },
  { id: 'role-dc-manager', code: 'dc_manager', name: 'Datacenter Manager', built_in: true },
  { id: 'role-auditor', code: 'auditor', name: 'Auditor', built_in: true },
]
const superAdminRole = roles[0] as Role

const permissions = [
  'datacenter:read', 'datacenter:create', 'datacenter:update', 'datacenter:delete',
  'rack:read', 'rack:create', 'rack:update', 'rack:delete',
  'device:read', 'device:create', 'device:update', 'device:delete',
  'user:read', 'user:create', 'user:update', 'user:delete',
  'role:read',
  'tenant:read', 'tenant:create', 'tenant:update', 'tenant:delete',
  'customer:read', 'customer:create', 'customer:update', 'customer:delete',
  'lifecycle:read', 'lifecycle:transition',
  'audit:read',
  'agent:read', 'agent:register', 'agent:manage', 'agent:version:read',
  'discovery:read', 'discovery:scan', 'discovery:confirm', 'discovery:ignore',
  'storage:read', 'storage:write',
  'provisioning:image:read', 'provisioning:image:write',
  'provisioning:install:read', 'provisioning:install:create', 'provisioning:install:activate',
  'provisioning:credential:read', 'provisioning:credential:reveal',
  'ipam:read', 'ipam:write', 'ipam:allocate',
  'remote:read', 'remote:probe', 'remote:connect', 'remote:close', 'remote:credential:write', 'remote:credential:reveal',
]

const bmcCredentialStatuses = new Map<string, BMCCredentialStatus>()
const bmcCredentialPasswords = new Map<string, string>()
const osCredentialStatuses = new Map<string, OSCredentialStatus>()
const osCredentialPasswords = new Map<string, string>()

const adminUser: User = {
  id: 'user-admin',
  username: 'admin',
  display_name: 'NEWDCIM Administrator',
  locale: 'zh-CN',
  active: true,
  roles: [superAdminRole],
}

let datacenters: Datacenter[] = [
  { id: 'dc-sha-01', tenant_id: 'tenant-default', code: '0001', name: 'Shanghai Core DC', address: 'Shanghai Zhangjiang', timezone: 'Asia/Shanghai', region_code: 'cn-east', status: 'active', created_at: now(), updated_at: now() },
  { id: 'dc-bjs-01', tenant_id: 'tenant-default', code: '0002', name: 'Beijing DR DC', address: 'Beijing Yizhuang', timezone: 'Asia/Shanghai', region_code: 'cn-north', status: 'maintenance', created_at: now(), updated_at: now() },
]
let nextDatacenterNumber = 3

let tenants: Tenant[] = [
  { id: 'tenant-default', code: 'default', name: '默认租户', status: 'active', plan: 'all', created_at: now(), updated_at: now() },
  { id: 'tenant-cloud', code: 'fastmos-cloud', name: 'Fastmos Cloud', status: 'active', plan: 'overseas', created_at: now(), updated_at: now() },
]

let customers: Customer[] = [
  { id: 'customer-001', tenant_id: 'tenant-default', code: '0001', name: 'Alice', account: 'alice', email: 'alice@example.com', phone: '+86 138 0000 0000', has_password: true, status: 'active', created_at: now(), updated_at: now() },
]
let nextCustomerNumber = 2

let racks: Rack[] = [
  { id: 'rack-sha-a01', datacenter_id: 'dc-sha-01', code: 'A01', name: 'Network Core Rack', total_units: 42, power_capacity_w: 12000, enabled_at: '2024-01-01' },
  { id: 'rack-sha-a02', datacenter_id: 'dc-sha-01', code: 'A02', name: 'Compute Rack', total_units: 42, power_capacity_w: 16000 },
  { id: 'rack-bjs-b01', datacenter_id: 'dc-bjs-01', code: 'B01', name: 'DR Rack', total_units: 42, power_capacity_w: 10000 },
]

let devices: Device[] = [
  { id: 'dev-fw-01', datacenter_id: 'dc-sha-01', rack_id: 'rack-sha-a01', asset_tag: 'FW-0001', name: 'Edge Firewall 01', vendor: 'Palo Alto', model: 'PA-5250', serial_number: 'DEMO-FW-01', cpu_summary: '', memory_gb: 0, disk_summary: '', gpu_summary: '', primary_mac: '', secondary_mac: '', public_ip: '', private_ip: '', gateway: '', netmask: '', dns_servers: '', switch_name: 'CE01', switch_port: 'Eth1/0/48', bmc_ip: '', bmc_username: '', bmc_type: '', bmc_status: '', management_note: '', start_unit: 38, height_units: 2, status: 'in_service', power_w: 650 },
  { id: 'dev-sw-01', datacenter_id: 'dc-sha-01', rack_id: 'rack-sha-a01', asset_tag: 'SW-0001', name: 'Core Switch 01', vendor: 'Cisco', model: 'Nexus 9336C', serial_number: 'DEMO-SW-01', cpu_summary: '', memory_gb: 0, disk_summary: '', gpu_summary: '', primary_mac: '', secondary_mac: '', public_ip: '', private_ip: '', gateway: '', netmask: '', dns_servers: '', switch_name: 'CE01', switch_port: 'Eth1/0/1', bmc_ip: '', bmc_username: '', bmc_type: '', bmc_status: '', management_note: '', start_unit: 34, height_units: 1, status: 'in_service', power_w: 420 },
  { id: 'dev-srv-01', datacenter_id: 'dc-sha-01', rack_id: 'rack-sha-a02', customer_id: 'customer-001', asset_tag: 'SRV-0001', name: 'Virtualization Host 01', vendor: 'Dell', model: 'R760', serial_number: 'DEMO-SRV-01', cpu_summary: '2 x Intel Xeon Silver', memory_gb: 256, disk_summary: '4 x SSD 960G', gpu_summary: '', primary_mac: '00:11:22:33:44:55', secondary_mac: '00:11:22:33:44:56', public_ip: '203.0.113.11', private_ip: '10.10.0.11', gateway: '203.0.113.1', netmask: '255.255.255.0', dns_servers: '8.8.8.8,1.1.1.1', switch_name: 'CE01', switch_port: 'Eth1/0/12', bmc_ip: '10.10.0.21', bmc_username: 'root', bmc_type: 'iDRAC', bmc_status: 'reachable', management_note: 'Demo inventory record; password is not stored here.', customer_note: 'Demo customer bare metal service.', service_expires_at: now(), start_unit: 20, height_units: 2, status: 'planned', power_w: 900 },
]

let ipPools: IPPool[] = [
  { id: 'pool-demo-public', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', name: '上海公网 IPv4', family: 'ipv4', cidr: '203.0.113.8/29', gateway: '203.0.113.9', dns_servers: '8.8.8.8,1.1.1.1', status: 'active', total: 5, available: 4, allocated: 1, reserved: 0, faulty: 0, created_at: now(), updated_at: now() },
]
let ipGroups: IPGroup[] = [
  { id: 'ip-group-demo-public', tenant_id: 'tenant-default', name: '公网 IP', note: '服务器主 IP / 附加 IP 资源分组', created_at: now(), updated_at: now() },
]
let ipAddresses: IPAddress[] = [
  { id: 'ip-demo-10', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', pool_id: 'pool-demo-public', address: '203.0.113.10', family: 'ipv4', status: 'available', hostname: '', note: '', created_at: now(), updated_at: now() },
  { id: 'ip-demo-11', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', pool_id: 'pool-demo-public', address: '203.0.113.11', family: 'ipv4', status: 'allocated', device_id: 'dev-srv-01', hostname: '', note: 'Demo allocated IP', allocated_at: now(), created_at: now(), updated_at: now() },
  { id: 'ip-demo-12', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', pool_id: 'pool-demo-public', address: '203.0.113.12', family: 'ipv4', status: 'available', hostname: '', note: '', created_at: now(), updated_at: now() },
  { id: 'ip-demo-13', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', pool_id: 'pool-demo-public', address: '203.0.113.13', family: 'ipv4', status: 'available', hostname: '', note: '', created_at: now(), updated_at: now() },
  { id: 'ip-demo-14', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', pool_id: 'pool-demo-public', address: '203.0.113.14', family: 'ipv4', status: 'available', hostname: '', note: '', created_at: now(), updated_at: now() },
]

let users: User[] = [adminUser]

const lifecycleStates: LifecycleState[] = [
  { id: 'state-purchased', code: 'purchased', name: 'Purchased', sort_order: 10, terminal: false, built_in: true },
  { id: 'state-stocked', code: 'stocked', name: 'Stocked', sort_order: 20, terminal: false, built_in: true },
  { id: 'state-testing', code: 'testing', name: 'Testing', sort_order: 30, terminal: false, built_in: true },
  { id: 'state-racked', code: 'racked', name: 'Racked', sort_order: 40, terminal: false, built_in: true },
  { id: 'state-installing', code: 'installing', name: 'Installing', sort_order: 50, terminal: false, built_in: true },
  { id: 'state-rented', code: 'rented', name: 'Rented', sort_order: 60, terminal: false, built_in: true },
  { id: 'state-maintenance', code: 'maintenance', name: 'Maintenance', sort_order: 70, terminal: false, built_in: true },
  { id: 'state-unracked', code: 'unracked', name: 'Unracked', sort_order: 80, terminal: false, built_in: true },
  { id: 'state-scrapped', code: 'scrapped', name: 'Scrapped', sort_order: 90, terminal: true, built_in: true },
]
let lifecycleEvents: LifecycleEvent[] = [
  { id: 'lifecycle-001', tenant_id: 'tenant-default', device_id: 'dev-srv-01', from_state: 'stocked', to_state: 'testing', reason: 'Demo hardware inspection started', actor_id: 'user-admin', request_id: 'mock-request', created_at: now() },
]
let operationLogs: OperationLog[] = [
  { id: 'op-001', tenant_id: 'tenant-default', user_id: 'user-admin', action: 'GET /api/v1/agents', resource_type: '', method: 'GET', path: '/api/v1/agents', request_id: 'mock-request', source_ip: '127.0.0.1', user_agent: 'Mock Browser', status_code: 200, result: 'success', error_code: '', created_at: now() },
]
const auditLogs: AuditLog[] = [
  { id: 'audit-001', tenant_id: 'tenant-default', actor_id: 'user-admin', event_type: 'tenant.created', severity: 'info', resource_type: 'tenant', resource_id: 'tenant-default', summary: 'Default tenant bootstrapped', metadata: { source: 'mock' }, created_at: now() },
]
const agents: Agent[] = [
  { id: 'agent-sha-core', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', name: 'whale-agent-sha01-core', public_ip: '156.235.107.131', fingerprint: 'local-dev-agent-fingerprint', mtls_subject: 'CN=whale-agent-sha01-core', version: '0.1.0', status: 'online', last_seen_at: now(), registered_at: now(), collector_diagnostics: { provider: 'readonly', status: 'ok', tools: [{ name: 'smartctl', command: 'smartctl', installed: true, path: '/usr/sbin/smartctl', required: true, allowed: true, checks: [{ args: ['--scan-open'], allowed: true }, { args: ['-a', '-j', '/dev/sda'], allowed: true }] }, { name: 'storcli64', command: '/opt/MegaRAID/storcli/storcli64', installed: true, path: '/opt/MegaRAID/storcli/storcli64', required: false, allowed: true, metadata: { category: 'raid' }, checks: [{ args: ['/call', 'show', 'J'], allowed: true }] }] } },
  { id: 'agent-bjs-core', tenant_id: 'tenant-default', datacenter_id: 'dc-bjs-01', name: 'whale-agent-bjs01-core', public_ip: '', fingerprint: 'demo-offline-agent', mtls_subject: 'CN=whale-agent-bjs01-core', version: '0.1.0', status: 'degraded', last_seen_at: new Date(Date.now() - 10 * 60_000).toISOString(), registered_at: now(), collector_diagnostics: { provider: 'readonly', status: 'degraded', tools: [{ name: 'smartctl', command: 'smartctl', installed: false, required: true, allowed: true, error: 'command not found', checks: [{ args: ['--scan-open'], allowed: true }] }, { name: 'ipmitool', command: 'ipmitool', installed: true, path: '/usr/bin/ipmitool', required: false, allowed: false, error: 'command not allowed by WHALE_AGENT_ALLOWED_COMMANDS', checks: [{ args: ['lan', 'print'], allowed: false }] }] } },
]
const agentControlIPs: Record<string, string> = {
  'agent-sha-core': '156.235.107.131',
  'agent-bjs-core': '10.20.0.11',
}
const agentCollectorRuns: AgentCollectorRun[] = [
  { id: 'collector-run-sha-001', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', agent_id: 'agent-sha-core', provider: 'readonly', status: 'ok', error_count: 0, warning_count: 0, diagnostics: agents[0]!.collector_diagnostics!, reported_at: now(), created_at: now() },
  { id: 'collector-run-bjs-001', tenant_id: 'tenant-default', datacenter_id: 'dc-bjs-01', agent_id: 'agent-bjs-core', provider: 'readonly', status: 'degraded', error_count: 1, warning_count: 1, diagnostics: agents[1]!.collector_diagnostics!, reported_at: new Date(Date.now() - 2 * 60_000).toISOString(), created_at: new Date(Date.now() - 2 * 60_000).toISOString() },
  { id: 'collector-run-bjs-000', tenant_id: 'tenant-default', datacenter_id: 'dc-bjs-01', agent_id: 'agent-bjs-core', provider: 'readonly', status: 'ok', error_count: 0, warning_count: 0, diagnostics: { provider: 'readonly', status: 'ok', tools: [] }, reported_at: new Date(Date.now() - 20 * 60_000).toISOString(), created_at: new Date(Date.now() - 20 * 60_000).toISOString() },
]
const agentCapabilities: AgentCapability[] = [
  { id: 'cap-sha-inventory', agent_id: 'agent-sha-core', capability: 'inventory', enabled: true, version: '0.1.0', last_reported_at: now() },
  { id: 'cap-sha-discovery', agent_id: 'agent-sha-core', capability: 'discovery', enabled: true, version: '0.1.0', last_reported_at: now() },
  { id: 'cap-sha-pxe', agent_id: 'agent-sha-core', capability: 'pxe', enabled: false, version: '0.1.0', last_reported_at: now() },
  { id: 'cap-sha-network', agent_id: 'agent-sha-core', capability: 'network', enabled: false, version: '0.1.0', last_reported_at: now() },
]
const agentVersions: AgentVersion[] = [
  { id: 'agent-version-010', version: '0.1.0', channel: 'stable', supported: true, min_master_version: '1.0.0', release_notes: 'Phase 1 Whale Agent Core', released_at: now() },
]
let discoveryJobs: DiscoveryJob[] = [
  { id: 'discovery-job-001', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', agent_id: 'agent-sha-core', scan_scope: { bmc_cidrs: ['10.10.0.0/24'], timeout_seconds: 300 }, status: 'succeeded', requested_by: 'user-admin', started_at: now(), finished_at: now(), error_message: '', created_at: now(), updated_at: now() },
]
let hardwareProfiles: HardwareProfile[] = [
  {
    id: 'hw-profile-001',
    tenant_id: 'tenant-default',
    serial_number: 'DEMO-R740-NEW-01',
    manufacturer: 'Dell',
    model: 'PowerEdge R740',
    cpu_summary: '2 x Intel Xeon Silver',
    cpu_cores: 32,
    memory_gb: 256,
    disk_summary: '4 x SSD 960G',
    raid_summary: 'RAID10',
    nic_summary: '4 x 10GbE',
    bmc_mac: '00:11:22:33:44:56',
    bmc_ip: '10.10.0.21',
    raw_object_key: '',
    discovered_at: now(),
    storage_snapshot: {
      controllers: [{ vendor: 'Broadcom', model: 'MegaRAID 9560-8i', serial_number: 'RAID-NEW-001', firmware_version: '52.16.0', driver_version: '8.7.1', controller_type: 'raid', status: 'ok' }],
      disks: [
        { controller_index: 0, slot: '0:0', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-NEW-0001', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 31, power_on_hours: 12, wear_level: 1, total_read_gb: 120, total_written_gb: 80, health_percent: 99, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 120, host_writes_gb: 80 } },
        { controller_index: 0, slot: '0:1', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-NEW-0002', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 31, power_on_hours: 12, wear_level: 1, total_read_gb: 130, total_written_gb: 88, health_percent: 99, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 130, host_writes_gb: 88 } },
        { controller_index: 0, slot: '0:2', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-NEW-0003', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 32, power_on_hours: 12, wear_level: 1, total_read_gb: 140, total_written_gb: 96, health_percent: 99, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 140, host_writes_gb: 96 } },
        { controller_index: 0, slot: '0:3', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-NEW-0004', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 32, power_on_hours: 12, wear_level: 1, total_read_gb: 150, total_written_gb: 104, health_percent: 99, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 150, host_writes_gb: 104 } },
      ],
      volumes: [{ controller_index: 0, name: 'Virtual Disk 0', raid_level: 'RAID10', capacity_gb: 1920, status: 'ok', disk_count: 4, stripe_size_kb: 256 }],
    },
  },
]
let serverDiscoveries: ServerDiscovery[] = [
  { id: 'server-discovery-001', tenant_id: 'tenant-default', datacenter_id: 'dc-sha-01', agent_id: 'agent-sha-core', discovery_job_id: 'discovery-job-001', hardware_profile_id: 'hw-profile-001', serial_number: 'DEMO-R740-NEW-01', mac_address: '00:11:22:33:44:55', bmc_ip: '10.10.0.21', switch_port_hint: 'CE6857 Eth1/0/12', status: 'pending_confirm', confidence: 95.5, raw_object_key: '', discovered_at: now() },
]
let storageControllers: StorageController[] = [
  { id: 'ctrl-dev-srv-01-raid', tenant_id: 'tenant-default', device_id: 'dev-srv-01', vendor: 'Broadcom', model: 'MegaRAID 9560-8i', serial_number: 'RAID-DEMO-001', firmware_version: '52.16.0', driver_version: '8.7.1', controller_type: 'raid', status: 'ok', created_at: now(), updated_at: now() },
]
let physicalDisks: PhysicalDisk[] = [
  { id: 'disk-dev-srv-01-0', tenant_id: 'tenant-default', device_id: 'dev-srv-01', controller_id: 'ctrl-dev-srv-01-raid', slot: '0:0', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-DEMO-0001', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 32, power_on_hours: 1234, wear_level: 8, total_read_gb: 8200, total_written_gb: 4100, health_percent: 92, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 8200, host_writes_gb: 4100 }, created_at: now(), updated_at: now() },
  { id: 'disk-dev-srv-01-1', tenant_id: 'tenant-default', device_id: 'dev-srv-01', controller_id: 'ctrl-dev-srv-01-raid', slot: '0:1', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-DEMO-0002', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 33, power_on_hours: 1230, wear_level: 7, total_read_gb: 7900, total_written_gb: 3900, health_percent: 93, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 7900, host_writes_gb: 3900 }, created_at: now(), updated_at: now() },
  { id: 'disk-dev-srv-01-2', tenant_id: 'tenant-default', device_id: 'dev-srv-01', controller_id: 'ctrl-dev-srv-01-raid', slot: '0:2', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-DEMO-0003', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'warning', smart_status: 'passed', temperature_c: 41, power_on_hours: 3900, wear_level: 71, total_read_gb: 42800, total_written_gb: 36100, health_percent: 29, raw_smart: { media_wearout_indicator: 29, host_reads_gb: 42800, host_writes_gb: 36100 }, created_at: now(), updated_at: now() },
  { id: 'disk-dev-srv-01-3', tenant_id: 'tenant-default', device_id: 'dev-srv-01', controller_id: 'ctrl-dev-srv-01-raid', slot: '0:3', vendor: 'Samsung', model: 'PM893', serial_number: 'SSD-DEMO-0004', media_type: 'ssd', protocol: 'sas', capacity_gb: 960, firmware_version: 'JXTC', status: 'ok', smart_status: 'passed', temperature_c: 34, power_on_hours: 1228, wear_level: 9, total_read_gb: 8400, total_written_gb: 4300, health_percent: 91, raw_smart: { reallocated_sector_count: 0, host_reads_gb: 8400, host_writes_gb: 4300 }, created_at: now(), updated_at: now() },
]
let logicalVolumes: LogicalVolume[] = [
  { id: 'vol-dev-srv-01-0', tenant_id: 'tenant-default', device_id: 'dev-srv-01', controller_id: 'ctrl-dev-srv-01-raid', name: 'Virtual Disk 0', raid_level: 'RAID10', capacity_gb: 1920, status: 'ok', disk_count: 4, stripe_size_kb: 256, created_at: now(), updated_at: now() },
]

export function createMockAdapter(): AxiosAdapter {
  return async (config) => {
    await delay(120)
    const method = (config.method || 'get').toUpperCase()
    const url = normalizeUrl(config)
    const path = url.pathname.replace(/^\/api\/v1/, '') || '/'

    if (method === 'POST' && path === '/auth/login') return login(config)
    if (method === 'POST' && path === '/auth/refresh') return ok(config, { data: authPayload() })
    if (method === 'POST' && path === '/auth/logout') return ok(config, { data: {} })
    if (method === 'GET' && path === '/users/me') return ok(config, { data: adminUser })
    if (method === 'GET' && path === '/roles') return ok(config, { data: roles })

    if (method === 'GET' && path === '/tenants') return page(config, tenants, url)
    if (method === 'POST' && path === '/tenants') return createTenant(config)
    if (method === 'PATCH' && /^\/tenants\/[^/]+$/.test(path)) return updateTenant(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/customers') return page(config, filterByTenant(customers, url), url)
    if (method === 'POST' && path === '/customers') return createCustomer(config)
    if (method === 'PATCH' && /^\/customers\/[^/]+$/.test(path)) return updateCustomer(config, path.split('/')[2]!)
    if (method === 'DELETE' && /^\/customers\/[^/]+$/.test(path)) return deleteCustomer(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/lifecycle/states') return ok(config, { data: lifecycleStates })
    if (method === 'GET' && path === '/lifecycle/events') return page(config, filterByTenant(lifecycleEvents, url), url)
    if (method === 'POST' && path === '/lifecycle/events') return createLifecycleEvent(config)
    if (method === 'GET' && path === '/operation-logs') return page(config, filterAuditResource(operationLogs, url), url)
    if (method === 'GET' && path === '/audit-logs') return page(config, filterAuditResource(auditLogs, url), url)
    if (method === 'GET' && path === '/agents') return page(config, filterAgents(agents, url), url)
    if (method === 'DELETE' && /^\/agents\/[^/]+$/.test(path)) return deleteAgentMock(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/agents/versions') return ok(config, { data: agentVersions })
    if (method === 'GET' && /^\/agents\/[^/]+\/capabilities$/.test(path)) return ok(config, { data: agentCapabilities.filter((capability) => capability.agent_id === path.split('/')[2]!) })
    if (method === 'GET' && /^\/agents\/[^/]+\/collector-runs$/.test(path)) return ok(config, { data: agentCollectorRuns.filter((run) => run.agent_id === path.split('/')[2]!).slice(0, Number(url.searchParams.get('limit') || 10)) })
    if (method === 'GET' && path === '/discovery/jobs') return page(config, filterDiscoveryJobs(discoveryJobs, url), url)
    if (method === 'POST' && path === '/discovery/jobs') return createDiscoveryJob(config)
    if (method === 'POST' && /^\/discovery\/jobs\/[^/]+\/cancel$/.test(path)) return cancelDiscoveryJob(config, path.split('/')[3]!)
    if (method === 'GET' && path === '/discovery/results') return page(config, filterDiscoveryResults(serverDiscoveries, url), url)
    if (method === 'POST' && /^\/discovery\/results\/[^/]+\/confirm$/.test(path)) return confirmDiscoveryResult(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/discovery\/results\/[^/]+\/ignore$/.test(path)) return ignoreDiscoveryResult(config, path.split('/')[3]!)
    if (method === 'GET' && /^\/hardware-profiles\/[^/]+$/.test(path)) return hardwareProfile(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/storage/controllers') return page(config, filterStorage(storageControllers, url), url)
    if (method === 'GET' && path === '/storage/disks') return page(config, filterStorage(physicalDisks, url), url)
    if (method === 'GET' && path === '/storage/volumes') return page(config, filterStorage(logicalVolumes, url), url)
    if (method === 'GET' && /^\/devices\/[^/]+\/storage$/.test(path)) return deviceStorage(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/ipam/groups') return page(config, ipGroups.map((group) => ({
      ...group,
      address_count: ipAddresses.filter((address) => address.group_id === group.id).length,
    })), url)
    if (method === 'POST' && path === '/ipam/groups') return createIPGroupMock(config)
    if (method === 'POST' && path === '/ipam/ranges') return createIPRangeMock(config)
    if (method === 'GET' && path === '/ipam/pools') return page(config, filterIPPools(ipPools, url), url)
    if (method === 'POST' && path === '/ipam/pools') return createIPPoolMock(config)
    if (method === 'POST' && /^\/ipam\/pools\/[^/]+\/addresses$/.test(path)) return createIPAddressMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/ipam\/pools\/[^/]+\/import-addresses$/.test(path)) return importIPAddressesMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/ipam\/pools\/[^/]+\/import-device-public-ips$/.test(path)) return importDevicePublicIPsMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/ipam\/pools\/[^/]+\/attach-device-public-ip$/.test(path)) return attachDevicePublicIPMock(config, path.split('/')[3]!)
    if (method === 'GET' && path === '/ipam/addresses') return page(config, filterIPAddresses(ipAddresses, url), url)
    if (method === 'POST' && /^\/ipam\/addresses\/[^/]+\/allocate$/.test(path)) return allocateIPAddressMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/ipam\/addresses\/[^/]+\/release$/.test(path)) return releaseIPAddressMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/ipam\/addresses\/[^/]+\/mark-faulty$/.test(path)) return markIPAddressMock(config, path.split('/')[3]!, 'faulty')
    if (method === 'POST' && /^\/ipam\/addresses\/[^/]+\/repair$/.test(path)) return markIPAddressMock(config, path.split('/')[3]!, 'available')

    if (method === 'GET' && path === '/datacenters') return page(config, datacenterSummaries(datacenters), url)
    if (method === 'POST' && path === '/datacenters') return createDatacenter(config)
    if (method === 'PATCH' && /^\/datacenters\/[^/]+$/.test(path)) return updateDatacenter(config, path.split('/')[2]!)
    if (method === 'DELETE' && /^\/datacenters\/[^/]+$/.test(path)) return deleteDatacenter(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/racks') return page(config, filterByDatacenter(racks, url), url)
    if (method === 'POST' && path === '/racks') return createRack(config)
    if (method === 'PATCH' && /^\/racks\/[^/]+$/.test(path)) return updateRack(config, path.split('/')[2]!)
    if (method === 'DELETE' && /^\/racks\/[^/]+$/.test(path)) return deleteRack(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/devices') return page(config, filterDevices(devices, url), url)
    if (method === 'POST' && path === '/devices') return createDevice(config)
    if (method === 'GET' && /^\/provisioning\/devices\/[^/]+\/os-credential$/.test(path)) return getOSCredentialMock(config, path.split('/')[3]!)
    if (method === 'POST' && /^\/provisioning\/devices\/[^/]+\/os-credential\/reveal$/.test(path)) return revealOSCredentialMock(config, path.split('/')[3]!)
    if (method === 'GET' && /^\/devices\/[^/]+\/bmc-credential$/.test(path)) return getBMCCredentialMock(config, path.split('/')[2]!)
    if (method === 'POST' && /^\/devices\/[^/]+\/bmc-credential\/reveal$/.test(path)) return revealBMCCredentialMock(config, path.split('/')[2]!)
    if (method === 'PUT' && /^\/devices\/[^/]+\/bmc-credential$/.test(path)) return putBMCCredentialMock(config, path.split('/')[2]!)
    if (method === 'DELETE' && /^\/devices\/[^/]+\/bmc-credential$/.test(path)) return deleteBMCCredentialMock(config, path.split('/')[2]!)
    if (method === 'PATCH' && /^\/devices\/[^/]+$/.test(path)) return updateDevice(config, path.split('/')[2]!)
    if (method === 'DELETE' && /^\/devices\/[^/]+$/.test(path)) return deleteDeviceMock(config, path.split('/')[2]!)
    if (method === 'GET' && path === '/users') return page(config, users, url)
    if (method === 'POST' && path === '/users') return createUser(config)

    return fail(config, 404, 'NOT_FOUND', 'Mock endpoint not found.')
  }
}

function login(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  if (body.username === 'admin' && body.password === adminPassword) return ok(config, { data: authPayload() })
  return fail(config, 401, 'INVALID_CREDENTIALS', 'Invalid username or password.')
}

function authPayload(): AuthPayload {
  const nowMs = Date.now()
  return {
    access_token: 'mock-access-token',
    token_type: 'Bearer',
    expires_at: new Date(nowMs + 15 * 60_000).toISOString(),
    refresh_token: 'mock-refresh-token',
    refresh_expires_at: new Date(nowMs + 7 * 24 * 60 * 60_000).toISOString(),
    user: adminUser,
    permissions,
  }
}

function createDatacenter(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: Datacenter = { id: id('dc'), tenant_id: body.tenant_id || 'tenant-default', code: String(nextDatacenterNumber++).padStart(4, '0'), name: body.name, address: body.address, timezone: body.timezone, region_code: body.region_code || '', status: body.status, created_at: now(), updated_at: now() }
  datacenters = [entity, ...datacenters]
  return ok(config, { data: entity }, 201)
}

function updateDatacenter(config: InternalAxiosRequestConfig, datacenterID: string) {
  const body = readBody(config.data)
  const index = datacenters.findIndex((item) => item.id === datacenterID)
  if (index < 0) return fail(config, 404, 'DATACENTER_NOT_FOUND', 'Datacenter not found.')
  const current = datacenters[index]!
  const updated: Datacenter = {
    ...current,
    name: body.name ?? current.name,
    address: body.address ?? current.address,
    timezone: body.timezone ?? current.timezone,
    region_code: body.region_code ?? current.region_code,
    status: body.status ?? current.status,
    updated_at: now(),
  }
  datacenters.splice(index, 1, updated)
  return ok(config, { data: updated })
}

function deleteDatacenter(config: InternalAxiosRequestConfig, datacenterID: string) {
  const hasResources = racks.some((rack) => rack.datacenter_id === datacenterID) ||
    devices.some((device) => device.datacenter_id === datacenterID) ||
    agents.some((agent) => agent.datacenter_id === datacenterID)
  if (hasResources) return fail(config, 409, 'DATACENTER_IN_USE', 'Datacenter contains resources.')
  datacenters = datacenters.filter((item) => item.id !== datacenterID)
  return ok(config, {}, 204)
}

function deleteAgentMock(config: InternalAxiosRequestConfig, agentID: string) {
  const index = agents.findIndex((agent) => agent.id === agentID)
  if (index < 0) return fail(config, 404, 'AGENT_NOT_FOUND', 'Agent not found.')
  agents.splice(index, 1)
  return ok(config, {}, 204)
}

function createTenant(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: Tenant = { id: id('tenant'), code: body.code, name: body.name, status: body.status, plan: body.plan || 'all', created_at: now(), updated_at: now() }
  tenants = [entity, ...tenants]
  return ok(config, { data: entity }, 201)
}

function updateTenant(config: InternalAxiosRequestConfig, tenantID: string) {
  const body = readBody(config.data)
  const index = tenants.findIndex((item) => item.id === tenantID)
  if (index < 0) return fail(config, 404, 'TENANT_NOT_FOUND', 'Tenant not found.')
  const current = tenants[index]!
  const updated: Tenant = {
    ...current,
    name: body.name ?? current.name,
    status: body.status ?? current.status,
    plan: body.plan ?? current.plan,
    updated_at: now(),
  }
  tenants.splice(index, 1, updated)
  return ok(config, { data: updated })
}

function createCustomer(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: Customer = { id: id('customer'), tenant_id: body.tenant_id || 'tenant-default', code: String(nextCustomerNumber++).padStart(4, '0'), name: body.name, account: body.account, email: body.email, phone: body.phone || '', has_password: Boolean(body.password), status: body.status, created_at: now(), updated_at: now() }
  customers = [entity, ...customers]
  return ok(config, { data: entity }, 201)
}

function updateCustomer(config: InternalAxiosRequestConfig, customerID: string) {
  const body = readBody(config.data)
  const index = customers.findIndex((item) => item.id === customerID)
  if (index < 0) return fail(config, 404, 'CUSTOMER_NOT_FOUND', 'Customer not found.')
  const current = customers[index]!
  const updated: Customer = {
    ...current,
    name: body.name ?? current.name,
    account: body.account ?? current.account,
    email: body.email ?? current.email,
    phone: body.phone ?? current.phone,
    has_password: body.password ? true : current.has_password,
    status: body.status ?? current.status,
    updated_at: now(),
  }
  customers.splice(index, 1, updated)
  return ok(config, { data: updated })
}

function deleteCustomer(config: InternalAxiosRequestConfig, customerID: string) {
  if (devices.some((device) => device.customer_id === customerID)) return fail(config, 409, 'CUSTOMER_IN_USE', 'Customer has assigned devices.')
  customers = customers.filter((item) => item.id !== customerID)
  return ok(config, {}, 204)
}

function createLifecycleEvent(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: LifecycleEvent = { id: id('lifecycle'), tenant_id: body.tenant_id, device_id: body.device_id || undefined, from_state: body.from_state || '', to_state: body.to_state, reason: body.reason || '', actor_id: 'user-admin', request_id: 'mock-request', created_at: now() }
  lifecycleEvents = [entity, ...lifecycleEvents]
  return ok(config, { data: entity }, 201)
}

function createRack(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: Rack = { id: id('rack'), datacenter_id: body.datacenter_id, code: body.code, name: body.name, total_units: Number(body.total_units), power_capacity_w: body.power_capacity_w === undefined || Number(body.power_capacity_w) === 0 ? 5000 : Number(body.power_capacity_w), enabled_at: body.enabled_at || undefined }
  racks = [entity, ...racks]
  return ok(config, { data: entity }, 201)
}

function updateRack(config: InternalAxiosRequestConfig, rackID: string) {
  const body = readBody(config.data)
  const index = racks.findIndex((item) => item.id === rackID)
  if (index < 0) return fail(config, 404, 'RACK_NOT_FOUND', 'Rack not found.')
  const maxOccupiedUnit = devices
    .filter((device) => device.rack_id === rackID)
    .reduce((max, device) => Math.max(max, device.start_unit + device.height_units - 1), 0)
  if (body.total_units !== undefined && Number(body.total_units) < maxOccupiedUnit) {
    return fail(config, 422, 'INVALID_PLACEMENT', 'Rack capacity is smaller than current device placement.')
  }
  const current = racks[index]!
  const updated: Rack = {
    ...current,
    name: body.name ?? current.name,
    total_units: body.total_units === undefined ? current.total_units : Number(body.total_units),
    power_capacity_w: body.power_capacity_w === undefined ? current.power_capacity_w : Number(body.power_capacity_w),
    enabled_at: body.enabled_at === undefined ? current.enabled_at : (body.enabled_at || undefined),
  }
  racks.splice(index, 1, updated)
  return ok(config, { data: updated })
}

function deleteRack(config: InternalAxiosRequestConfig, rackID: string) {
  if (devices.some((device) => device.rack_id === rackID)) return fail(config, 409, 'RACK_IN_USE', 'Rack contains devices.')
  racks = racks.filter((item) => item.id !== rackID)
  return ok(config, {}, 204)
}

function createDevice(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const entity: Device = { id: id('dev'), customer_id: body.customer_id || undefined, datacenter_id: body.datacenter_id, rack_id: body.rack_id, asset_tag: body.asset_tag, name: body.name, vendor: body.vendor || '', model: body.model || '', serial_number: body.serial_number || '', cpu_summary: body.cpu_summary || '', memory_gb: Number(body.memory_gb || 0), disk_summary: body.disk_summary || '', gpu_summary: body.gpu_summary || '', primary_mac: normalizeMAC(body.primary_mac), secondary_mac: normalizeMAC(body.secondary_mac), public_ip: body.public_ip || '', private_ip: body.private_ip || '', gateway: body.gateway || '', netmask: body.netmask || '', dns_servers: body.dns_servers || '', switch_name: body.switch_name || '', switch_port: body.switch_port || '', bmc_ip: body.bmc_ip || '', bmc_username: body.bmc_username || '', bmc_type: body.bmc_type || '', bmc_status: body.bmc_status || '', management_note: body.management_note || '', customer_note: body.customer_note || '', service_started_at: body.service_started_at || undefined, service_expires_at: body.service_expires_at || undefined, start_unit: Number(body.start_unit), height_units: Number(body.height_units), status: body.status, power_w: Number(body.power_w) }
  devices = [entity, ...devices]
  return ok(config, { data: entity }, 201)
}

function updateDevice(config: InternalAxiosRequestConfig, deviceID: string) {
  const body = readBody(config.data)
  const index = devices.findIndex((item) => item.id === deviceID)
  if (index < 0) return fail(config, 404, 'DEVICE_NOT_FOUND', 'Device not found.')
  const current = devices[index]!
  const targetRackID = body.rack_id ?? current.rack_id
  const targetRack = racks.find((item) => item.id === targetRackID)
  if (!targetRack) return fail(config, 404, 'RACK_NOT_FOUND', 'Rack not found.')
  const targetDatacenterID = body.datacenter_id ?? (body.rack_id ? targetRack.datacenter_id : current.datacenter_id)
  const targetStartUnit = body.start_unit === undefined ? current.start_unit : Number(body.start_unit)
  const targetHeightUnits = body.height_units === undefined ? current.height_units : Number(body.height_units)
  if (targetRack.datacenter_id !== targetDatacenterID || targetStartUnit + targetHeightUnits - 1 > targetRack.total_units) {
    return fail(config, 422, 'INVALID_PLACEMENT', 'Device does not fit in this rack.')
  }
  const hasOverlap = devices.some((device) => (
    device.id !== deviceID &&
    device.rack_id === targetRackID &&
    device.start_unit <= targetStartUnit + targetHeightUnits - 1 &&
    device.start_unit + device.height_units - 1 >= targetStartUnit
  ))
  if (hasOverlap) return fail(config, 409, 'RACK_UNIT_CONFLICT', 'Rack units are already occupied.')
  const updated: Device = {
    ...current,
    datacenter_id: targetDatacenterID,
    rack_id: targetRackID,
    name: body.name ?? current.name,
    vendor: body.vendor ?? current.vendor,
    model: body.model ?? current.model,
    serial_number: body.serial_number ?? current.serial_number,
    cpu_summary: body.cpu_summary ?? current.cpu_summary ?? '',
    memory_gb: body.memory_gb === undefined ? current.memory_gb || 0 : Number(body.memory_gb),
    disk_summary: body.disk_summary ?? current.disk_summary ?? '',
    gpu_summary: body.gpu_summary ?? current.gpu_summary ?? '',
    primary_mac: body.primary_mac === undefined ? current.primary_mac ?? '' : normalizeMAC(body.primary_mac),
    secondary_mac: body.secondary_mac === undefined ? current.secondary_mac ?? '' : normalizeMAC(body.secondary_mac),
    public_ip: body.public_ip ?? current.public_ip ?? '',
    private_ip: body.private_ip ?? current.private_ip ?? '',
    gateway: body.gateway ?? current.gateway ?? '',
    netmask: body.netmask ?? current.netmask ?? '',
    dns_servers: body.dns_servers ?? current.dns_servers ?? '',
    switch_name: body.switch_name ?? current.switch_name ?? '',
    switch_port: body.switch_port ?? current.switch_port ?? '',
    bmc_ip: body.bmc_ip ?? current.bmc_ip ?? '',
    bmc_username: body.bmc_username ?? current.bmc_username ?? '',
    bmc_type: body.bmc_type ?? current.bmc_type ?? '',
    bmc_status: body.bmc_status ?? current.bmc_status ?? '',
    management_note: body.management_note ?? current.management_note ?? '',
    customer_id: body.customer_id === undefined ? current.customer_id : body.customer_id || undefined,
    customer_note: body.customer_note ?? current.customer_note ?? '',
    service_started_at: body.service_started_at === undefined ? current.service_started_at : body.service_started_at || undefined,
    service_expires_at: body.service_expires_at === undefined ? current.service_expires_at : body.service_expires_at || undefined,
    start_unit: targetStartUnit,
    height_units: targetHeightUnits,
    status: body.status ?? current.status,
    power_w: body.power_w === undefined ? current.power_w : Number(body.power_w),
  }
  devices.splice(index, 1, updated)
  return ok(config, { data: updated })
}

function deleteDeviceMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const exists = devices.some((item) => item.id === deviceID)
  if (!exists) return fail(config, 404, 'DEVICE_NOT_FOUND', 'Device not found.')
  devices = devices.filter((item) => item.id !== deviceID)
  return ok(config, {}, 204)
}

function createUser(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const assigned = roles.filter((role) => Array.isArray(body.roles) && body.roles.includes(role.code))
  const entity: User = { id: id('user'), username: body.username, display_name: body.display_name, locale: body.locale || 'zh-CN', active: true, roles: assigned }
  users = [entity, ...users]
  return ok(config, { data: entity }, 201)
}

function createDiscoveryJob(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const agent = agents.find((item) => item.id === body.agent_id)
  if (!agent || agent.status !== 'online' || agent.collector_diagnostics?.status === 'degraded') {
    return fail(config, 409, 'AGENT_COLLECTOR_NOT_READY', 'Agent collector is not ready for discovery.')
  }
  const entity: DiscoveryJob = { id: id('discovery-job'), tenant_id: body.tenant_id || 'tenant-default', datacenter_id: body.datacenter_id, agent_id: body.agent_id, scan_scope: body.scan_scope || {}, status: 'pending', requested_by: 'user-admin', error_message: '', created_at: now(), updated_at: now() }
  discoveryJobs = [entity, ...discoveryJobs]
  return ok(config, { data: entity }, 201)
}

function cancelDiscoveryJob(config: InternalAxiosRequestConfig, jobID: string) {
  discoveryJobs = discoveryJobs.map((job) => job.id === jobID ? { ...job, status: 'canceled', finished_at: now(), updated_at: now() } : job)
  return ok(config, { data: {} }, 204)
}

function confirmDiscoveryResult(config: InternalAxiosRequestConfig, resultID: string) {
  const body = readBody(config.data)
  const result = serverDiscoveries.find((item) => item.id === resultID)
  if (!result) return fail(config, 404, 'DISCOVERY_NOT_FOUND', 'Discovery result not found.')
  const profile = hardwareProfiles.find((item) => item.id === result.hardware_profile_id)
  const deviceID = id('dev')
  const device: Device = { id: deviceID, tenant_id: result.tenant_id, datacenter_id: result.datacenter_id, rack_id: body.rack_id, asset_tag: body.asset_tag, name: body.name, vendor: profile?.manufacturer || '', model: profile?.model || '', serial_number: result.serial_number, cpu_summary: profile?.cpu_summary || '', memory_gb: profile?.memory_gb || 0, disk_summary: profile?.disk_summary || '', gpu_summary: '', primary_mac: normalizeMAC(result.mac_address), secondary_mac: '', public_ip: '', private_ip: '', gateway: '', netmask: '', dns_servers: '', switch_name: '', switch_port: result.switch_port_hint || '', bmc_ip: result.bmc_ip || profile?.bmc_ip || '', bmc_username: '', bmc_type: '', bmc_status: result.bmc_ip || profile?.bmc_ip ? 'discovered' : '', management_note: '', start_unit: Number(body.start_unit), height_units: Number(body.height_units), status: body.status || 'planned', lifecycle_state: 'testing', power_w: Number(body.power_w || 0) }
  if (profile?.id) device.hardware_profile_id = profile.id
  devices = [device, ...devices]
  if (profile?.storage_snapshot) createStorageFromSnapshot(result.tenant_id, deviceID, profile.storage_snapshot)
  serverDiscoveries = serverDiscoveries.map((item) => item.id === resultID ? { ...item, status: 'confirmed', confirmed_device_id: deviceID, confirmed_at: now() } : item)
  return ok(config, { data: serverDiscoveries.find((item) => item.id === resultID) })
}

function createStorageFromSnapshot(tenantID: string, deviceID: string, snapshot: NonNullable<HardwareProfile['storage_snapshot']>) {
  const controllerIDs: string[] = []
  for (const controller of snapshot.controllers || []) {
    const controllerID = id('ctrl')
    controllerIDs.push(controllerID)
    storageControllers = [{
      id: controllerID,
      tenant_id: tenantID,
      device_id: deviceID,
      vendor: controller.vendor || '',
      model: controller.model || '',
      serial_number: controller.serial_number || '',
      firmware_version: controller.firmware_version || '',
      driver_version: controller.driver_version || '',
      controller_type: controller.controller_type || 'unknown',
      status: controller.status || 'unknown',
      created_at: now(),
      updated_at: now(),
    }, ...storageControllers]
  }
  for (const disk of snapshot.disks || []) {
    const controllerID = controllerIDs[disk.controller_index || 0]
    physicalDisks = [{
      id: id('disk'),
      tenant_id: tenantID,
      device_id: deviceID,
      ...(controllerID ? { controller_id: controllerID } : {}),
      slot: disk.slot || '',
      vendor: disk.vendor || '',
      model: disk.model || '',
      serial_number: disk.serial_number || '',
      media_type: disk.media_type || 'unknown',
      protocol: disk.protocol || 'unknown',
      capacity_gb: disk.capacity_gb || 0,
      firmware_version: disk.firmware_version || '',
      status: disk.status || 'unknown',
      smart_status: disk.smart_status || 'unknown',
      temperature_c: disk.temperature_c || 0,
      power_on_hours: disk.power_on_hours || 0,
      wear_level: disk.wear_level || 0,
      total_read_gb: disk.total_read_gb || 0,
      total_written_gb: disk.total_written_gb || 0,
      health_percent: disk.health_percent || (disk.wear_level !== undefined ? Math.max(0, 100 - disk.wear_level) : 100),
      raw_smart: disk.raw_smart || {},
      created_at: now(),
      updated_at: now(),
    }, ...physicalDisks]
  }
  for (const volume of snapshot.volumes || []) {
    const controllerID = controllerIDs[volume.controller_index || 0]
    logicalVolumes = [{
      id: id('vol'),
      tenant_id: tenantID,
      device_id: deviceID,
      ...(controllerID ? { controller_id: controllerID } : {}),
      name: volume.name || '',
      raid_level: volume.raid_level || 'unknown',
      capacity_gb: volume.capacity_gb || 0,
      status: volume.status || 'unknown',
      disk_count: volume.disk_count || 0,
      stripe_size_kb: volume.stripe_size_kb || 0,
      created_at: now(),
      updated_at: now(),
    }, ...logicalVolumes]
  }
}

function ignoreDiscoveryResult(config: InternalAxiosRequestConfig, resultID: string) {
  serverDiscoveries = serverDiscoveries.map((item) => item.id === resultID ? { ...item, status: 'ignored', ignored_at: now() } : item)
  return ok(config, { data: serverDiscoveries.find((item) => item.id === resultID) })
}

function hardwareProfile(config: InternalAxiosRequestConfig, profileID: string) {
  const profile = hardwareProfiles.find((item) => item.id === profileID)
  if (!profile) return fail(config, 404, 'HARDWARE_PROFILE_NOT_FOUND', 'Hardware profile not found.')
  return ok(config, { data: profile })
}

function deviceStorage(config: InternalAxiosRequestConfig, deviceID: string) {
  return ok(config, {
    data: {
      device_id: deviceID,
      controllers: storageControllers.filter((item) => item.device_id === deviceID),
      disks: physicalDisks.filter((item) => item.device_id === deviceID),
      volumes: logicalVolumes.filter((item) => item.device_id === deviceID),
    },
  })
}

function page<T>(config: InternalAxiosRequestConfig, rows: T[], url: URL) {
  const search = (url.searchParams.get('search') || '').toLowerCase()
  const filtered = search ? rows.filter((row) => JSON.stringify(row).toLowerCase().includes(search)) : rows
  const pageNumber = Math.max(Number(url.searchParams.get('page') || 1), 1)
  const pageSize = Math.min(Math.max(Number(url.searchParams.get('page_size') || 20), 1), 100)
  const start = (pageNumber - 1) * pageSize
  const body: Page<T> = { data: filtered.slice(start, start + pageSize), meta: { page: pageNumber, page_size: pageSize, total: filtered.length, total_pages: Math.max(Math.ceil(filtered.length / pageSize), 1) } }
  return ok(config, body)
}

function filterByDatacenter<T extends { datacenter_id: string }>(rows: T[], url: URL) {
  const datacenterID = url.searchParams.get('datacenter_id')
  const rackID = url.searchParams.get('rack_id')
  let result = datacenterID ? rows.filter((row) => row.datacenter_id === datacenterID) : rows
  if (rackID) result = result.filter((row) => !('rack_id' in row) || row.rack_id === rackID)
  return result
}

function filterDevices(rows: Device[], url: URL) {
  const customerID = url.searchParams.get('customer_id')
  let result = filterByDatacenter(rows, url)
  if (customerID) result = result.filter((row) => row.customer_id === customerID)
  return result
}

function refreshIPPoolStats() {
  ipPools = ipPools.map((pool) => {
    const rows = ipAddresses.filter((address) => address.pool_id === pool.id)
    return {
      ...pool,
      total: rows.length,
      available: rows.filter((address) => address.status === 'available').length,
      allocated: rows.filter((address) => address.status === 'allocated').length,
      reserved: rows.filter((address) => address.status === 'reserved').length,
      faulty: rows.filter((address) => address.status === 'faulty').length,
    }
  })
}

function filterIPPools(rows: IPPool[], url: URL) {
  refreshIPPoolStats()
  const datacenterID = url.searchParams.get('datacenter_id')
  return datacenterID ? rows.filter((row) => row.datacenter_id === datacenterID) : rows
}

function filterIPAddresses(rows: IPAddress[], url: URL) {
  const poolID = url.searchParams.get('pool_id')
  const deviceID = url.searchParams.get('device_id')
  const groupID = url.searchParams.get('group_id')
  const status = url.searchParams.get('status')
  let result = poolID ? rows.filter((row) => row.pool_id === poolID) : rows
  if (deviceID) result = result.filter((row) => row.device_id === deviceID)
  if (groupID) result = result.filter((row) => row.group_id === groupID)
  if (status) result = result.filter((row) => row.status === status)
  return result
}

function createIPGroupMock(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const name = String(body.name || '').trim()
  if (!name) return fail(config, 400, 'VALIDATION_FAILED', 'IP group name is required')
  if (ipGroups.some((item) => item.name.toLowerCase() === name.toLowerCase())) return fail(config, 409, 'IP_GROUP_EXISTS', 'IP group already exists')
  const entity: IPGroup = {
    id: id('ip-group'),
    tenant_id: 'tenant-default',
    name,
    note: String(body.note || '').trim(),
    created_at: now(),
    updated_at: now(),
  }
  ipGroups = [entity, ...ipGroups]
  return ok(config, { data: entity }, 201)
}

function createIPRangeMock(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const group = ipGroups.find((item) => item.id === body.group_id)
  const dc = datacenters.find((item) => item.id === body.datacenter_id)
  if (!group || !dc) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP group or datacenter not found')
  const rule = String(body.rule || 'custom_range')
  const type = (body.ip_type || 'public') as IPAddressType
  const autoAssign = body.auto_assign !== false
  const dns = [body.dns1, body.dns2].map((item) => String(item || '').trim()).filter(Boolean).join(',')
  let values: string[] = []
  let cidr = String(body.cidr || '').trim()
  if (rule === 'cidr') {
    values = mockIPv4Hosts(cidr)
  } else {
    values = mockIPv4Range(String(body.start_ip || ''), String(body.end_ip || ''))
    const prefix = String(body.netmask || '').includes('/24') || String(body.netmask || '') === '255.255.255.0' ? '/24' : '/24'
    cidr = values[0] ? `${values[0].split('.').slice(0, 3).join('.')}.0${prefix}` : ''
  }
  if (!values.length) return fail(config, 422, 'INVALID_IP_RANGE', 'invalid IP range')
  let gateway = ''
  if (body.gateway_mode === 'first') {
    gateway = values.shift() || ''
  } else if (body.gateway_mode === 'last') {
    gateway = values.pop() || ''
  }
  if (!values.length) return fail(config, 422, 'INVALID_IP_RANGE', 'no assignable IP')
  const pool: IPPool = {
    id: id('pool'),
    tenant_id: 'tenant-default',
    group_id: group.id,
    datacenter_id: dc.id,
    name: `${group.name} / ${cidr}`,
    family: 'ipv4',
    cidr,
    gateway,
    gateway_mode: body.gateway_mode || 'first',
    netmask: body.netmask || '',
    vlan_id: body.vlan_id,
    dns_servers: dns,
    ip_type: type,
    auto_assign: autoAssign,
    status: 'active',
    total: 0,
    available: 0,
    allocated: 0,
    reserved: 0,
    faulty: 0,
    created_at: now(),
    updated_at: now(),
  }
  ipPools = [pool, ...ipPools]
  const created: string[] = []
  for (const address of values) {
    if (ipAddresses.some((item) => item.pool_id === pool.id && item.address === address)) continue
    ipAddresses.unshift({
      id: id('ip'),
      tenant_id: pool.tenant_id,
      group_id: group.id,
      datacenter_id: dc.id,
      pool_id: pool.id,
      address,
      family: 'ipv4',
      status: 'available',
      ip_type: type,
      auto_assign: autoAssign,
      hostname: '',
      note: body.note || '',
      created_at: now(),
      updated_at: now(),
    })
    created.push(address)
  }
  refreshIPPoolStats()
  const current = ipPools.find((item) => item.id === pool.id) || pool
  return ok(config, { data: { pool: current, result: { created: created.length, updated: 0, skipped: 0, errors: [], imported: created } } }, 201)
}

function createIPPoolMock(config: InternalAxiosRequestConfig) {
  const body = readBody(config.data)
  const pool: IPPool = { id: id('pool'), tenant_id: 'tenant-default', group_id: body.group_id, datacenter_id: body.datacenter_id, name: body.name, family: 'ipv4', cidr: body.cidr, gateway: body.gateway || '', gateway_mode: body.gateway_mode || 'first', netmask: body.netmask || '', vlan_id: body.vlan_id, dns_servers: body.dns_servers || '', ip_type: body.ip_type || 'public', auto_assign: body.auto_assign !== false, status: body.status || 'active', total: 0, available: 0, allocated: 0, reserved: 0, faulty: 0, created_at: now(), updated_at: now() }
  ipPools = [pool, ...ipPools]
  const generated = mockIPv4Hosts(String(body.cidr || '')).map((address) => ({ id: id('ip'), tenant_id: 'tenant-default', group_id: pool.group_id, datacenter_id: pool.datacenter_id, pool_id: pool.id, address, family: 'ipv4' as const, status: 'available' as const, ip_type: pool.ip_type, auto_assign: pool.auto_assign, hostname: '', note: '', created_at: now(), updated_at: now() }))
  ipAddresses = [...generated, ...ipAddresses]
  refreshIPPoolStats()
  return ok(config, { data: ipPools.find((item) => item.id === pool.id) }, 201)
}

function createIPAddressMock(config: InternalAxiosRequestConfig, poolID: string) {
  const body = readBody(config.data)
  const pool = ipPools.find((item) => item.id === poolID)
  if (!pool) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP pool not found')
  const address = String(body.address || '').trim()
  if (!address) return fail(config, 400, 'VALIDATION_FAILED', 'IP address is required')
  if (ipAddresses.some((item) => item.pool_id === poolID && item.address === address)) return fail(config, 409, 'IP_ADDRESS_EXISTS', 'IP address already exists')
  const status = (['available', 'reserved', 'faulty'].includes(String(body.status || '')) ? body.status : 'available') as IPAddress['status']
  const entity: IPAddress = {
    id: id('ip'),
    group_id: pool.group_id,
    datacenter_id: pool.datacenter_id,
    pool_id: pool.id,
    address,
    family: pool.family,
    status,
    ip_type: pool.ip_type,
    auto_assign: pool.auto_assign,
    hostname: '',
    note: body.note || '',
    created_at: now(),
    updated_at: now(),
  }
  if (pool.tenant_id) entity.tenant_id = pool.tenant_id
  ipAddresses = [entity, ...ipAddresses]
  refreshIPPoolStats()
  return ok(config, { data: entity }, 201)
}

function importIPAddressesMock(config: InternalAxiosRequestConfig, poolID: string) {
  const body = readBody(config.data)
  const pool = ipPools.find((item) => item.id === poolID)
  if (!pool) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP pool not found')
  const status = (['available', 'reserved', 'faulty'].includes(String(body.status || '')) ? body.status : 'available') as IPAddress['status']
  const values = Array.from(new Set(String(body.addresses || '').split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean)))
  const result = { created: 0, updated: 0, skipped: 0, errors: [] as string[], imported: [] as string[] }
  for (const address of values) {
    if (ipAddresses.some((item) => item.pool_id === poolID && item.address === address)) {
      result.skipped += 1
      continue
    }
    const entity: IPAddress = {
      id: id('ip'),
      group_id: pool.group_id,
      datacenter_id: pool.datacenter_id,
      pool_id: pool.id,
      address,
      family: pool.family,
      status,
      ip_type: pool.ip_type,
      auto_assign: pool.auto_assign,
      hostname: '',
      note: body.note || '',
      created_at: now(),
      updated_at: now(),
    }
    if (pool.tenant_id) entity.tenant_id = pool.tenant_id
    ipAddresses.unshift(entity)
    result.created += 1
    result.imported.push(address)
  }
  refreshIPPoolStats()
  return ok(config, { data: result })
}

function importDevicePublicIPsMock(config: InternalAxiosRequestConfig, poolID: string) {
  const body = readBody(config.data)
  const pool = ipPools.find((item) => item.id === poolID)
  if (!pool) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP pool not found')
  const result = { created: 0, updated: 0, skipped: 0, errors: [] as string[], imported: [] as string[] }
  const rows = devices.filter((device) => device.datacenter_id === pool.datacenter_id && device.public_ip)
  for (const device of rows) {
    const publicIP = String(device.public_ip || '').trim()
    const existingIndex = ipAddresses.findIndex((item) => item.pool_id === poolID && item.address === publicIP)
    if (existingIndex >= 0) {
      const existing = ipAddresses[existingIndex]!
      if (existing.status === 'allocated' && existing.device_id === device.id) {
        result.skipped += 1
        continue
      }
      if (existing.status !== 'available') {
        result.errors.push(`${publicIP} 当前状态不是空闲，无法绑定 ${device.asset_tag}`)
        continue
      }
      ipAddresses.splice(existingIndex, 1, { ...existing, status: 'allocated', device_id: device.id, note: body.note || '', allocated_at: now(), updated_at: now() })
      result.updated += 1
      result.imported.push(publicIP)
      continue
    }
    const entity: IPAddress = {
      id: id('ip'),
      datacenter_id: pool.datacenter_id,
      pool_id: pool.id,
      address: publicIP,
      family: pool.family,
      status: 'allocated',
      device_id: device.id,
      hostname: '',
      note: body.note || '',
      allocated_at: now(),
      created_at: now(),
      updated_at: now(),
    }
    if (pool.tenant_id) entity.tenant_id = pool.tenant_id
    ipAddresses.unshift(entity)
    result.created += 1
    result.imported.push(publicIP)
  }
  refreshIPPoolStats()
  return ok(config, { data: result })
}

function attachDevicePublicIPMock(config: InternalAxiosRequestConfig, poolID: string) {
  const body = readBody(config.data)
  const pool = ipPools.find((item) => item.id === poolID)
  if (!pool) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP pool not found')
  const device = devices.find((item) => item.id === body.device_id)
  if (!device) return fail(config, 404, 'DEVICE_NOT_FOUND', 'Device not found')
  const publicIP = String(device.public_ip || '').trim()
  if (!publicIP) return fail(config, 422, 'DEVICE_PUBLIC_IP_MISSING', 'device public IP is empty')
  const existingIndex = ipAddresses.findIndex((item) => item.pool_id === poolID && item.address === publicIP)
  if (existingIndex >= 0) {
    const existing = ipAddresses[existingIndex]!
    if (existing.status === 'allocated' && existing.device_id === device.id) return ok(config, { data: existing })
    if (existing.status !== 'available') return fail(config, 409, 'IP_ADDRESS_IN_USE', 'IP address is not available')
    const updated = { ...existing, status: 'allocated' as const, device_id: device.id, note: body.note || '', allocated_at: now(), updated_at: now() }
    ipAddresses.splice(existingIndex, 1, updated)
    Object.assign(device, { public_ip: updated.address, gateway: pool.gateway || device.gateway, dns_servers: pool.dns_servers || device.dns_servers })
    refreshIPPoolStats()
    return ok(config, { data: updated })
  }
  const entity: IPAddress = {
    id: id('ip'),
    datacenter_id: pool.datacenter_id,
    pool_id: pool.id,
    address: publicIP,
    family: pool.family,
    status: 'allocated',
    device_id: device.id,
    hostname: '',
    note: body.note || '',
    allocated_at: now(),
    created_at: now(),
    updated_at: now(),
  }
  if (pool.tenant_id) entity.tenant_id = pool.tenant_id
  ipAddresses.unshift(entity)
  Object.assign(device, { public_ip: entity.address, gateway: pool.gateway || device.gateway, dns_servers: pool.dns_servers || device.dns_servers })
  refreshIPPoolStats()
  return ok(config, { data: entity })
}

function allocateIPAddressMock(config: InternalAxiosRequestConfig, ipID: string) {
  const body = readBody(config.data)
  const index = ipAddresses.findIndex((item) => item.id === ipID)
  if (index < 0) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP not found')
  const current = ipAddresses[index]!
  if (current.status !== 'available') return fail(config, 409, 'IP_ADDRESS_IN_USE', 'IP address is not available')
  const device = devices.find((item) => item.id === body.device_id)
  if (!device) return fail(config, 404, 'DEVICE_NOT_FOUND', 'Device not found')
  const updated = { ...current, status: 'allocated' as const, device_id: device.id, note: body.note || '', allocated_at: now(), updated_at: now() }
  ipAddresses.splice(index, 1, updated)
  Object.assign(device, { public_ip: updated.address, gateway: ipPools.find((item) => item.id === updated.pool_id)?.gateway || device.gateway })
  refreshIPPoolStats()
  return ok(config, { data: updated })
}

function releaseIPAddressMock(config: InternalAxiosRequestConfig, ipID: string) {
  const index = ipAddresses.findIndex((item) => item.id === ipID)
  if (index < 0) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP not found')
  const current = ipAddresses[index]!
  if (current.status !== 'allocated') return fail(config, 409, 'IP_ADDRESS_NOT_ALLOCATED', 'IP address is not allocated')
  if (current.device_id) {
    const device = devices.find((item) => item.id === current.device_id)
    if (device && device.public_ip === current.address) device.public_ip = ''
  }
  const { device_id: _deviceID, ...released } = current
  const updated: IPAddress = { ...released, status: 'available', note: readBody(config.data).note || '', released_at: now(), updated_at: now() }
  ipAddresses.splice(index, 1, updated)
  refreshIPPoolStats()
  return ok(config, { data: updated })
}

function markIPAddressMock(config: InternalAxiosRequestConfig, ipID: string, status: 'available' | 'faulty') {
  const index = ipAddresses.findIndex((item) => item.id === ipID)
  if (index < 0) return fail(config, 404, 'IPAM_RESOURCE_NOT_FOUND', 'IP not found')
  const current = ipAddresses[index]!
  if (current.status === 'allocated') return fail(config, 409, 'IP_ALLOCATED_TO_DEVICE', 'Allocated IP must be released first')
  const updated = { ...current, status, note: readBody(config.data).note || '', updated_at: now() }
  ipAddresses.splice(index, 1, updated)
  refreshIPPoolStats()
  return ok(config, { data: updated })
}

function getBMCCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const device = devices.find((item) => item.id === deviceID)
  if (!device) return fail(config, 404, 'REMOTE_DEVICE_NOT_FOUND', 'device not found')
  return ok(config, { data: bmcCredentialStatuses.get(deviceID) || { device_id: deviceID, configured: false, username: device.bmc_username || '', status: '' } })
}

function putBMCCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const device = devices.find((item) => item.id === deviceID)
  if (!device) return fail(config, 404, 'REMOTE_DEVICE_NOT_FOUND', 'device not found')
  const body = readBody(config.data)
  if (!String(body.username || '').trim() || !String(body.password || '')) return fail(config, 422, 'VALIDATION_ERROR', 'username and password are required')
  const status: BMCCredentialStatus = { device_id: deviceID, configured: true, username: String(body.username).trim(), status: 'active', updated_at: now() }
  bmcCredentialStatuses.set(deviceID, status)
  bmcCredentialPasswords.set(deviceID, String(body.password))
  return ok(config, { data: status })
}

function revealBMCCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const status = bmcCredentialStatuses.get(deviceID)
  if (!status?.configured) return fail(config, 404, 'REMOTE_CREDENTIAL_MISSING', 'BMC credential is not configured')
  return ok(config, { data: { ...status, password: bmcCredentialPasswords.get(deviceID) || 'mock-bmc-password' } })
}

function deleteBMCCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  bmcCredentialStatuses.delete(deviceID)
  bmcCredentialPasswords.delete(deviceID)
  return ok(config, undefined, 204)
}

function getOSCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const device = devices.find((item) => item.id === deviceID)
  if (!device) return fail(config, 404, 'DEVICE_OS_CREDENTIAL_NOT_FOUND', 'device not found')
  return ok(config, {
    data: osCredentialStatuses.get(deviceID) || {
      device_id: deviceID,
      tenant_id: device.tenant_id || 'tenant-default',
      configured: false,
      os_family: '',
      username: '',
      remote_protocol: '',
      remote_port: 0,
      status: '',
    },
  })
}

function revealOSCredentialMock(config: InternalAxiosRequestConfig, deviceID: string) {
  const device = devices.find((item) => item.id === deviceID)
  if (!device) return fail(config, 404, 'DEVICE_OS_CREDENTIAL_NOT_FOUND', 'device not found')
  if (!osCredentialStatuses.has(deviceID)) {
    const status: OSCredentialStatus = {
      device_id: deviceID,
      tenant_id: device.tenant_id || 'tenant-default',
      configured: true,
      os_family: 'windows',
      username: 'Administrator',
      remote_protocol: 'rdp',
      remote_port: device.rdp_port || 3389,
      status: 'active',
      updated_at: now(),
    }
    osCredentialStatuses.set(deviceID, status)
    osCredentialPasswords.set(deviceID, 'Mock-Windows-Password-2026!')
  }
  const status = osCredentialStatuses.get(deviceID)!
  const revealed = { ...status, revealed_at: now() }
  osCredentialStatuses.set(deviceID, revealed)
  return ok(config, { data: { ...revealed, password: osCredentialPasswords.get(deviceID) || 'Mock-Windows-Password-2026!' } })
}

function mockIPv4Hosts(cidr: string) {
  const match = cidr.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/)
  if (!match) return []
  const prefix = Number(match[5])
  if (prefix < 24 || prefix > 30) return []
  const base = match.slice(1, 5).map(Number)
  if (base.length !== 4 || base.some((part) => Number.isNaN(part) || part < 0 || part > 255)) return []
  const count = Math.min(2 ** (32 - prefix) - 2, 128)
  const [a, b, c, d] = base as [number, number, number, number]
  return Array.from({ length: Math.max(count, 0) }, (_, idx) => `${a}.${b}.${c}.${d + idx + 1}`)
}

function mockIPv4Range(start: string, end: string) {
  const parse = (value: string): [number, number, number, number] | null => {
    const parts = value.trim().split('.').map(Number)
    if (parts.length !== 4 || parts.some((part) => Number.isNaN(part) || part < 0 || part > 255)) return null
    return [parts[0]!, parts[1]!, parts[2]!, parts[3]!]
  }
  const from = parse(start)
  const to = parse(end)
  if (!from || !to || from.slice(0, 3).join('.') !== to.slice(0, 3).join('.') || to[3] < from[3]) return []
  const count = Math.min(to[3] - from[3] + 1, 128)
  return Array.from({ length: count }, (_, idx) => `${from[0]}.${from[1]}.${from[2]}.${from[3] + idx}`)
}

function datacenterSummaries(rows: Datacenter[]): Datacenter[] {
  return rows.map((datacenter) => {
    const dcAgents = agents.filter((agent) => agent.datacenter_id === datacenter.id)
    return {
      ...datacenter,
      rack_count: racks.filter((rack) => rack.datacenter_id === datacenter.id).length,
      device_count: devices.filter((device) => device.datacenter_id === datacenter.id).length,
      agent_count: dcAgents.length,
      online_agent_count: dcAgents.filter((agent) => agent.status === 'online').length,
      control_agents: dcAgents.map((agent) => ({
        agent_id: agent.id,
        name: agent.name,
        public_ip: agent.public_ip || '',
        control_ip: agentControlIPs[agent.id] || '',
        status: agent.status,
        version: agent.version,
        ...(agent.last_seen_at ? { last_seen_at: agent.last_seen_at } : {}),
      })),
    }
  })
}

function filterByTenant<T extends { tenant_id?: string }>(rows: T[], url: URL) {
  const tenantID = url.searchParams.get('tenant_id')
  return tenantID ? rows.filter((row) => row.tenant_id === tenantID) : rows
}

function filterAuditResource<T extends { tenant_id?: string; resource_type?: string; resource_id?: string }>(rows: T[], url: URL) {
  let result = filterByTenant(rows, url)
  const resourceType = url.searchParams.get('resource_type')
  const resourceID = url.searchParams.get('resource_id')
  if (resourceType) result = result.filter((row) => row.resource_type === resourceType)
  if (resourceID) result = result.filter((row) => row.resource_id === resourceID)
  return result
}

function filterAgents(rows: Agent[], url: URL) {
  let result = filterByTenant(rows, url)
  const datacenterID = url.searchParams.get('datacenter_id')
  const status = url.searchParams.get('status')
  if (datacenterID) result = result.filter((row) => row.datacenter_id === datacenterID)
  if (status) result = result.filter((row) => row.status === status)
  return result
}

function filterDiscoveryJobs(rows: DiscoveryJob[], url: URL) {
  let result = filterByTenant(rows, url)
  const datacenterID = url.searchParams.get('datacenter_id')
  const agentID = url.searchParams.get('agent_id')
  const status = url.searchParams.get('status')
  if (datacenterID) result = result.filter((row) => row.datacenter_id === datacenterID)
  if (agentID) result = result.filter((row) => row.agent_id === agentID)
  if (status) result = result.filter((row) => row.status === status)
  return result
}

function filterDiscoveryResults(rows: ServerDiscovery[], url: URL) {
  let result = filterByTenant(rows, url)
  const datacenterID = url.searchParams.get('datacenter_id')
  const agentID = url.searchParams.get('agent_id')
  const jobID = url.searchParams.get('discovery_job_id')
  const status = url.searchParams.get('status')
  if (datacenterID) result = result.filter((row) => row.datacenter_id === datacenterID)
  if (agentID) result = result.filter((row) => row.agent_id === agentID)
  if (jobID) result = result.filter((row) => row.discovery_job_id === jobID)
  if (status) result = result.filter((row) => row.status === status)
  return result
}

function filterStorage<T extends { tenant_id: string; device_id: string; status?: string }>(rows: T[], url: URL) {
  let result = filterByTenant(rows, url)
  const deviceID = url.searchParams.get('device_id')
  const status = url.searchParams.get('status')
  if (deviceID) result = result.filter((row) => row.device_id === deviceID)
  if (status) result = result.filter((row) => row.status === status)
  return result
}

function normalizeUrl(config: InternalAxiosRequestConfig) {
  const raw = config.url || '/'
  const base = raw.startsWith('http') ? undefined : `http://newdcim.local${config.baseURL || ''}`
  const url = new URL(raw, base)
  for (const [key, value] of Object.entries((config.params || {}) as Record<string, unknown>)) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
  }
  return url
}

function readBody(data: unknown): Record<string, any> {
  if (!data) return {}
  if (typeof data === 'string') return JSON.parse(data)
  return data as Record<string, any>
}

function normalizeMAC(value: unknown) {
  return String(value || '').trim().toLowerCase()
}

function ok(config: InternalAxiosRequestConfig, data: unknown, status = 200): Promise<AxiosResponse> {
  return Promise.resolve({ data, status, statusText: 'OK', headers: {}, config })
}

function fail(config: InternalAxiosRequestConfig, status: number, code: string, message: string) {
  const response = { data: { error: { code, message, request_id: 'mock-request' } }, status, statusText: message, headers: {}, config }
  return Promise.reject({ isAxiosError: true, config, response, message })
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function now() {
  return new Date().toISOString()
}

function id(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
}
